import { getSalesStores } from "@/api/profile";
import { DataTable } from "@/components/dashboard";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
    GridActionsCellItem,
    GridColDef,
    GridRowParams,
} from "@mui/x-data-grid";
import { useAsync } from "@react-hookz/web";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/components/context/useAuth";

import { useFilterModel } from "@/hooks";

interface IStoreRow {
    id: number;
    name: string;
    store_address: string;
}

export const SalesStoresTable = () => {
    const { user } = useAuth();

    const navigate = useNavigate();
    const [storeRows, setStoreRows] = useState<IStoreRow[]>([]);
    const [getSalesStoresState, getSalesStoresActions] =
        useAsync(getSalesStores);

    const { filterModel, handleFilterModelChange } = useFilterModel();

    // set default pagination model
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    // Obtain list of stores that the sales user is assigned to
    useEffect(() => {
        getSalesStoresActions.execute(
            paginationModel.page + 1,
            paginationModel.pageSize,
            filterModel
        );
    }, [
        getSalesStoresActions,
        paginationModel.page,
        paginationModel.pageSize,
        filterModel,
    ]);

    useEffect(() => {
        if (
            getSalesStoresState.status === "success" &&
            getSalesStoresState.result
        ) {
            const data = getSalesStoresState.result.items.map((store) => {
                return {
                    id: store.id,
                    name: store.name,
                    store_address: [
                        store.store_address.address1,
                        store.store_address.address2,
                        store.store_address.city,
                        store.store_address.state,
                        store.store_address.zip_code,
                    ]
                        .filter(Boolean)
                        .join(", "),
                } as IStoreRow;
            });

            setStoreRows(data);
        }
    }, [getSalesStoresState]);

    const autosizeOptions = {
        columns: ["name", "store_address"],
        includeHeaders: true,
        includeOutliers: true,
        expand: true,
    };

    // Column Definition for the Table
    const columns: GridColDef<IStoreRow>[] = useMemo(
        () => [
            {
                field: "id",
                headerName: "ID",
                display: "flex",
                sortingOrder: ["desc", "asc"],
            },
            {
                field: "name",
                headerName: "Store Name",
                display: "flex",
            },
            {
                field: "store_address",
                headerName: "Store Address",
                display: "flex",
            },
            {
                field: "actions",
                type: "actions",
                getActions: (params: GridRowParams) => {
                    const actions = [];
                    // if admin, render all
                    // if sales, render only for stores that you "represent"
                    if (user?.is_sales || user?.is_admin) {
                        actions.push(
                            <GridActionsCellItem
                                icon={<VisibilityIcon />}
                                label="View store"
                                onClick={() =>
                                    navigate(
                                        `/dashboard/stores/${params.row.id}/orders`
                                    )
                                }
                            />
                        );
                    }
                    // Admins can edit
                    if (user?.is_sales || user?.is_admin) {
                        actions.push(
                            <GridActionsCellItem
                                icon={<EditIcon />}
                                label="Edit store"
                                onClick={() =>
                                    navigate(
                                        `/dashboard/stores/${params.row.id}/edit`
                                    )
                                }
                            />
                        );
                    }
                    return actions;
                },
            },
        ],
        [navigate, user]
    );

    // memoize rowCount to avoid resetting the page to 0 when the data is loading
    const rowCountRef = useRef(getSalesStoresState.result?.total || 0);

    const rowCount = useMemo(() => {
        if (getSalesStoresState.result?.total !== undefined) {
            rowCountRef.current = getSalesStoresState.result?.total;
        }
        return rowCountRef.current;
    }, [getSalesStoresState.result?.total]);

    return (
        <>
            <DataTable
                loading={getSalesStoresState.status === "loading"}
                rows={storeRows}
                columns={columns}
                initialState={{
                    sorting: {
                        sortModel: [{ field: "id", sort: "desc" }],
                    },
                }}
                autoSize
                autosizeOptions={autosizeOptions}
                onFilterModelChange={handleFilterModelChange}
                onSortModelChange={handleFilterModelChange}
                filterMode={"server"}
                sortingMode={"server"}
                paginationMode={"server"}
                rowCount={rowCount}
                pageSizeOptions={[10, 25, 50]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
            />
        </>
    );
};

export default SalesStoresTable;
